<script setup lang="ts">
import type { ChatSessionResponse } from "@agent-service-types/index.ts";
import { format, parseISO } from "date-fns";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Divider from "primevue/divider";
import Dropdown from "primevue/dropdown";
import OverlayPanel from "primevue/overlaypanel";
import Rating from "primevue/rating";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";
import { sessionTitleSchema } from "@/components/MyStudentDetail/AdvisingSession/AdvisingSessions/types";
import ViewDetails from "@/components/MyStudentDetail/AdvisingSession/ViewDetails/index.vue";
import { useGetChatSessions } from "@/providers/chatSessions/useGetChatSessions";
import { useUpdateChatSession } from "@/providers/chatSessions/useUpdateChatSession";
import { useFilters } from "../composables/useFilters";
import { useSearchStringFilter } from "../composables/useSearchStringFilter";
import { useSortFilters } from "../composables/useSortFilters";
import {
  COLUMN_OPTIONS,
  COLUMNS_NOT_TO_ENABLE_AT_LOAD,
  ROWS_PER_PAGE_DROPDOWN_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
} from "./constants";
import { anonymousAdvisingTablePresets, ratingPt, viewDetailButtonPt } from "./styles";
import type { Props } from "./types";
import { useShowColumns } from "./useShowColumns";

defineOptions({
  name: "AnonymousAdvisingTable",
});

const props = withDefaults(defineProps<Props>(), {});

const columnSelectorPanel = ref();
const pageLimit = ref<number>(ROWS_PER_PAGE_OPTIONS[0]);
const pageSkip = ref<number>(0);
const currentPage = ref<number>(1);
const currentFirst = ref<number>(0);

const toast = useToast();

const { searchString } = useSearchStringFilter();
const { isAnonymous, ratingFeedback, email, firstName, lastName, title } = useFilters();
const { orderBy, orderDirection } = useSortFilters();

const { displayableColumns, canDisplayColumn, columnOptions } = useShowColumns(
  COLUMN_OPTIONS,
  COLUMNS_NOT_TO_ENABLE_AT_LOAD,
);

const params = computed(() => ({
  limit: pageLimit.value,
  offset: pageSkip.value,
  isAnonymous: isAnonymous.value,
  ratingFeedback: ratingFeedback.value,
  email: email.value,
  firstName: firstName.value,
  lastName: lastName.value,
  title: title.value,
  searchString: searchString.value || null,
  orderBy: orderBy.value,
  orderDirection: orderDirection.value,
}));

const { isLoading, data, isError } = useGetChatSessions(params, {
  refetchOnWindowFocus: false,
  throwOnError: (error) => {
    toast.add({
      severity: "error",
      summary: "Sessions",
      detail: `Failed to load sessions. ${error.message}`,
      group: "br-aa",
      life: 5000,
    });
    return false;
  },
});

const expandedRows = ref<Record<string, boolean>>({});

// ── Title inline editing ──────────────────────────────────────────────────

const editingSessionId = ref<string | undefined>(undefined);
const editTitle = ref<string>("");
const titleErrorMessage = ref<string>("");
const titleOverrides = ref<Record<string, string>>({});

const { mutate: updateSession } = useUpdateChatSession();

const sessionTitle = (session: ChatSessionResponse): string =>
  titleOverrides.value[session.id] ??
  session.title ??
  `Advising Session on ${formatDate(session.created_at ?? "")}`.trimEnd();

const startEdit = (session: ChatSessionResponse): void => {
  editingSessionId.value = session.id;
  editTitle.value = sessionTitle(session);
  titleErrorMessage.value = "";
};

const commitEdit = (session: ChatSessionResponse): void => {
  if (editingSessionId.value === session.id) {
    const result = sessionTitleSchema.safeParse(editTitle.value.trim());
    if (!result.success) {
      titleErrorMessage.value = result.error.issues[0].message;
    } else {
      editingSessionId.value = undefined;
      titleErrorMessage.value = "";
      titleOverrides.value = { ...titleOverrides.value, [session.id]: result.data };
      updateSession(
        { chatSessionId: session.id, payload: { title: result.data } },
        {
          onSuccess: () => {
            const { [session.id]: _, ...rest } = titleOverrides.value;
            titleOverrides.value = rest;
            toast.add({
              severity: "success",
              group: "br-aa",
              summary: "Title updated",
              detail: "Session title has been saved.",
              life: 3000,
            });
          },
          onError: () => {
            const { [session.id]: _, ...rest } = titleOverrides.value;
            titleOverrides.value = rest;
            toast.add({
              severity: "error",
              group: "br-aa",
              summary: "Update failed",
              detail: "Could not save the session title. Please try again.",
              life: 5000,
            });
          },
        },
      );
    }
  }
};

const cancelEdit = (): void => {
  editingSessionId.value = undefined;
  titleErrorMessage.value = "";
};

// ── View Details modal ────────────────────────────────────────────────────

const viewDetailsVisible = ref<boolean>(false);
const selectedSession = ref<ChatSessionResponse | undefined>(undefined);

const openViewDetails = (session: ChatSessionResponse): void => {
  selectedSession.value = session;
  viewDetailsVisible.value = true;
};

const formatDate = (dateStr: string | undefined): string => (dateStr ? format(parseISO(dateStr), "M/d/yyyy") : "");

const toggleColumnSelectorPanel = (event: Event): void => {
  columnSelectorPanel.value?.toggle(event);
};

const pageCount = computed<number>(() => Math.ceil((data.value?.total ?? 0) / pageLimit.value));

const pageDropDownOptions = computed(() =>
  Array.from({ length: pageCount.value }, (_, i) => ({ key: `${i + 1}`, value: i + 1 })),
);

const dropdownPageChange = (value: number): void => {
  const newPageSkip = (value - 1) * pageLimit.value;
  if (pageSkip.value !== newPageSkip) {
    pageSkip.value = newPageSkip;
  }
  currentPage.value = value;
  currentFirst.value = newPageSkip;
};

const dataTablePageChange = (event: { page: number; rows: number }): void => {
  const newPageSkip = event.page * pageLimit.value;
  if (pageSkip.value !== newPageSkip) {
    pageSkip.value = newPageSkip;
  }
  if (event.rows !== pageLimit.value) {
    pageLimit.value = event.rows;
  }
  currentPage.value = event.page + 1;
  currentFirst.value = newPageSkip;
};
</script>

<template>
  <div class="mt-6">
    <Toast position="bottom-right" group="br-aa" />
    <div class="flex justify-between items-center h-8 my-4">
      <div class="align-content-end flex items-center ml-auto">
        <div class="mr-2">{{ data?.total ?? 0 }} Items</div>
        <Dropdown
          :model-value="currentPage"
          @update:modelValue="dropdownPageChange"
          option-label="key"
          option-value="value"
          :options="pageDropDownOptions"
        />
        <div class="ml-2">of {{ pageCount }}</div>
        <Divider class="!m-3" layout="vertical" />
        <div>View</div>
        <Dropdown
          class="ml-2"
          v-model="pageLimit"
          option-label="key"
          option-value="value"
          :options="ROWS_PER_PAGE_DROPDOWN_OPTIONS"
        />
        <Button link @click="toggleColumnSelectorPanel">
          <span class="material-symbols-outlined">settings</span>
        </Button>
        <OverlayPanel ref="columnSelectorPanel">
          <div class="flex flex-col">
            <div
              v-for="columnOption of columnOptions"
              :key="columnOption.key"
              class="flex items-center mb-4"
            >
              <Checkbox
                v-model="displayableColumns"
                :inputId="columnOption.key"
                :name="columnOption.key"
                :value="columnOption.key"
              />
              <label :for="columnOption.key" class="ml-4">{{ columnOption.name }}</label>
            </div>
          </div>
        </OverlayPanel>
      </div>
    </div>
    <ViewDetails
      :visible="viewDetailsVisible"
      :title="selectedSession?.title ?? ''"
      :user-name="`${selectedSession?.first_name ?? ''} ${selectedSession?.last_name ?? ''}`.trim()"
      :interactions="selectedSession?.chat_interactions ?? []"
      @update:visible="viewDetailsVisible = $event"
    />
    <DataTable
      data-test-id="anonymous-advising-table"
      :value="data?.data ?? []"
      :pt="anonymousAdvisingTablePresets.datatable"
      paginator
      :always-show-paginator="true"
      :rows="pageLimit"
      :totalRecords="data?.total ?? 0"
      :rowsPerPageOptions="ROWS_PER_PAGE_OPTIONS"
      :loading="isLoading"
      dataKey="id"
      resizableColumns
      columnResizeMode="fit"
      tableStyle="min-width: 50rem"
      :lazy="true"
      :first="currentFirst"
      v-model:expandedRows="expandedRows"
      @page="dataTablePageChange"
    >
      <Column expander style="width: 3rem" />
      <Column v-if="canDisplayColumn('id')" header="Id" field="id" column-key="id" class="w-[200px]" />
      <Column v-if="canDisplayColumn('title')" header="Title" column-key="title" class="w-[260px]">
        <template #body="slotProps">
          <div class="flex flex-col">
            <template v-if="editingSessionId === slotProps.data.id">
              <input
                v-model="editTitle"
                type="text"
                class="border rounded px-1 py-0.5 text-xs bg-white focus:outline-none focus:ring-1"
                :class="titleErrorMessage ? 'border-red-500 ring-red-500' : 'border-gray-300 focus:ring-teal-500'"
                :ref="(el) => el && (el as HTMLInputElement).focus()"
                @blur="commitEdit(slotProps.data)"
                @keydown.enter="commitEdit(slotProps.data)"
                @keydown.escape="cancelEdit"
                @click.stop
              />
              <span v-if="titleErrorMessage" class="text-xs text-red-500 mt-0.5">{{ titleErrorMessage }}</span>
            </template>
            <span
              v-else
              class="cursor-pointer hover:underline"
              @click.stop="startEdit(slotProps.data)"
            >{{ sessionTitle(slotProps.data) }}</span>
          </div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('name')" header="Name" column-key="name" class="w-[200px]">
        <template #body="slotProps">
          <div>{{ `${slotProps.data.first_name ?? ""} ${slotProps.data.last_name ?? ""}`.trim() }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('email')" header="Email" field="email" column-key="email" class="w-[220px]" />
      <Column v-if="canDisplayColumn('is_anonymous')" header="Anonymous" column-key="is_anonymous" class="w-[120px]">
        <template #body="slotProps">
          <div>{{ slotProps.data.is_anonymous ? "Yes" : "No" }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('summary')" header="Summary" field="summary" column-key="summary" class="w-[300px]">
        <template #body="slotProps">
          <div class="truncate max-w-[280px]">{{ slotProps.data.summary }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('rating_feedback')" header="Rating" column-key="rating_feedback" class="w-[150px]">
        <template #body="slotProps">
          <Rating
            :model-value="slotProps.data.rating_feedback ?? 0"
            :readonly="true"
            :cancel="false"
            :pt="ratingPt"
          />
        </template>
      </Column>
      <Column v-if="canDisplayColumn('text_feedback')" header="Feedback" column-key="text_feedback" class="w-[260px]">
        <template #body="slotProps">
          <div class="truncate max-w-[240px]">{{ slotProps.data.text_feedback }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('topics')" header="Topics" column-key="topics" class="w-[100px]">
        <template #body="slotProps">
          <div>{{ slotProps.data.chat_sessions_advising_topics?.length ?? 0 }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('created_at')" header="Date" column-key="created_at" class="w-[120px]">
        <template #body="slotProps">
          <div>{{ formatDate(slotProps.data.created_at) }}</div>
        </template>
      </Column>
      <Column header="View Details" column-key="view_details" class="w-[120px]">
        <template #body="slotProps">
          <Button :pt="viewDetailButtonPt" class="p-button-outlined" @click="openViewDetails(slotProps.data)">
            <div class="flex items-center">
              <span class="material-symbols-outlined text-sm mr-1 leading-5">open_in_new</span>
              <div>View Detail</div>
            </div>
          </Button>
        </template>
      </Column>
      <template #expansion="slotProps">
        <div class="grid grid-cols-2 gap-6 px-4 py-3 bg-gray-50">
          <div>
            <div class="text-[10px] font-bold tracking-widest text-gray-600 uppercase mb-1">Summary</div>
            <div class="text-sm text-gray-900 font-medium leading-relaxed">{{ slotProps.data.summary }}</div>
          </div>
          <div>
            <div class="text-[10px] font-bold tracking-widest text-gray-600 uppercase mb-1">Feedback</div>
            <div class="text-sm text-gray-900 font-medium leading-relaxed">{{ slotProps.data.text_feedback }}</div>
          </div>
        </div>
      </template>
      <template #empty>
        No sessions found.
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
</style>
